import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { usePatient } from "../context/PatientContext";
import { useTheme } from "../context/ThemeContext";
import AddNewVisit from "./Add/AddNewVisit";
import CONFIG from "../config";
import { motion, AnimatePresence } from "framer-motion";
import {
  Stethoscope,
  Wallet,
  History,
  ChevronRight,
  Calendar,
  Loader2,
  ListRestart,
  ArrowLeft,
  User,
  ClipboardList
} from "lucide-react";

const PatientVisits = () => {
  const { patientId, caseId } = useParams();
  const { verifyTokenAndRedirect } = useAuth();
  const { theme, isDarkMode } = useTheme();
  const { patientData, setPatientData, fetchVisitsForCase, setSelectedVisit, doctorInfo } = usePatient();
  const location = useLocation();

  const [visits, setVisits] = useState([]);
  const [patientName, setPatientName] = useState(location.state?.patientName || "");
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const baseURL = CONFIG.BASE_URL;
  const token = localStorage.getItem("authToken");

  /**
   * 🔹 Ensure Authentication
   */
  useEffect(() => {
    verifyTokenAndRedirect();
  }, [verifyTokenAndRedirect]);

  /**
   * 🔹 Fetch Balance
   */
  const fetchBalance = useCallback(async () => {
    try {
      const response = await axios.get(`${baseURL}/patients/${patientId}/balance`, {
        headers: {
          Authorization: `Bearer ${token}`,
          doctorId: doctorInfo?.MANDALI_ID,
          clinicId: doctorInfo?.CLINIC_ID,
          roleId: doctorInfo?.MANDALI_ROLE,
        },
      });
      setBalance(response.data.previous_balance || 0);
    } catch (error) {
      console.error("Error fetching patient balance:", error);
      setBalance(0);
    }
  }, [baseURL, patientId, token, doctorInfo]);

  /**
   * 🔹 Fetch Patient Info (to get name if visits list is empty)
   */
  const fetchPatientInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${baseURL}/patients/${patientId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          doctorId: doctorInfo?.MANDALI_ID,
          clinicId: doctorInfo?.CLINIC_ID,
          roleId: doctorInfo?.MANDALI_ROLE,
        },
      });
      if (response.data) {
        setPatientName(response.data.PATIENT_NAME);
      }
    } catch (error) {
      console.error("Error fetching patient info:", error);
    }
  }, [baseURL, patientId, token, doctorInfo]);

  /**
   * 🔹 Fetch Visits for Selected Case
   */
  useEffect(() => {
    const loadVisits = async () => {
      setLoading(true);
      try {
        const data = await fetchVisitsForCase(patientId, caseId);
        setVisits(data || []);
        if (data && data.length > 0 && data[0].PATIENT_NAME) {
          setPatientName(data[0].PATIENT_NAME);
        } else if (!patientName) {
          // If no visits yet or name missing, and we don't have it from state, fetch patient details
          fetchPatientInfo();
        }
      } catch (error) {
        console.error("Error fetching visits:", error);
      } finally {
        setLoading(false);
      }
    };

    loadVisits();
    fetchBalance();
  }, [location.key, fetchVisitsForCase, patientId, caseId, fetchBalance, fetchPatientInfo]);

  /**
   * 🔹 Handle Selecting a Visit
   */
  const handleVisitSelect = useCallback(
    (visitId) => {
      const updatedPatientData = { ...patientData, ACTION_STATUS: "UPDATE_EXISTING_VISIT" };
      setPatientData(updatedPatientData);
      setSelectedVisit(visitId);
      navigate(`/visits/${patientId}/${caseId}/${visitId}`);
    },
    [navigate, patientId, caseId, patientData, setPatientData, setSelectedVisit]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
        <Loader2 className="animate-spin" size={48} style={{ color: "#ffffff" }} />
      </div>
    );
  }

  const tableHeaderStyle = {
    backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    color: theme.text,
    borderColor: theme.border,
    fontWeight: "700",
    textTransform: "uppercase",
    fontSize: "11px",
    letterSpacing: "0.15em",
    padding: "1.25rem 2rem",
    borderBottom: `2px solid ${theme.border}`,
    textAlign: "left"
  };

  const tableCellStyle = {
    color: theme.text,
    borderColor: theme.border,
    fontSize: "15px",
    padding: "1.25rem 2rem",
    borderBottom: `1px solid ${theme.border}`
  };

  return (
    <div className="min-h-screen p-4 md:p-8 transition-colors duration-500" style={{ background: 'transparent' }}>
      <div className="max-w-[1400px] mx-auto">

        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(`/patients/${patientId}`)}
          className="mb-6 flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all hover:scale-105 active:scale-95 shadow-lg border"
          style={{
            backgroundColor: "rgba(255,255,255,0.1)",
            color: "#ffffff",
            borderColor: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)"
          }}
        >
          <ArrowLeft size={18} />
          To Patient File
        </motion.button>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
        >
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-[2rem] flex items-center justify-center shadow-2xl border"
              style={{ backgroundColor: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.3)", backdropFilter: "blur(10px)" }}>
              <User size={32} color="#ffffff" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-serif mb-1" style={{ color: "#ffffff", fontFamily: theme.fonts?.serif }}>
                {patientName || "Patient Visits"}
              </h2>
              <p className="text-lg opacity-80 flex items-center gap-3" style={{ color: "#ffffff" }}>
                <span className="px-2 py-0.5 rounded-md bg-white/10 text-sm font-mono">ID: {patientId}</span>
                <span className="px-2 py-0.5 rounded-md bg-white/10 text-sm font-mono">Case: #{caseId}</span>
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="px-6 py-4 rounded-3xl border shadow-xl flex flex-col items-end"
              style={{ backgroundColor: "rgba(255,255,255,0.1)", borderColor: "rgba(255,255,255,0.2)", backdropFilter: "blur(10px)" }}>
              <span className="text-xs uppercase tracking-widest opacity-60 font-bold mb-1" style={{ color: "#ffffff" }}>Current Balance</span>
              <div className="flex items-center gap-2">
                <Wallet size={20} color="#ffffff" className="opacity-60" />
                <span className="text-2xl font-bold" style={{ color: balance >= 0 ? "#ffffff" : "#ff7675" }}>
                  ₹ {balance?.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Bar & Add Visit Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-2xl" style={{ backgroundColor: theme.secondary + '40', color: "#ffffff" }}>
                <ClipboardList size={22} />
              </div>
              <h3 className="text-2xl font-bold" style={{ color: "#ffffff" }}>Case Visits History</h3>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-[2.5rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] border"
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" style={{ backgroundColor: theme.inputBg }}>
                  <thead>
                    <tr style={{ backgroundColor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)' }}>
                      <th style={tableHeaderStyle}>Visit Details & Highlights</th>
                      <th style={tableHeaderStyle}>Date</th>
                      <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: theme.border }}>
                    {visits.length > 0 ? (
                      visits.map((visit, idx) => (
                        <motion.tr
                          key={visit.VISIT_ID}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="group cursor-pointer transition-colors"
                          onClick={() => handleVisitSelect(visit.VISIT_ID)}
                          style={{
                            borderBottom: `1px solid ${theme.border}`,
                            backgroundColor: isDarkMode ? theme.inputBg : 'inherit'
                          }}
                          whileHover={{
                            backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)"
                          }}
                        >
                          <td style={tableCellStyle}>
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg border shadow-sm shrink-0"
                                style={{ backgroundColor: theme.primary + '15', color: theme.primary, borderColor: theme.primary + '20' }}>
                                <Stethoscope size={24} />
                              </div>
                              <div className="truncate max-w-md">
                                <p className="font-bold text-lg" style={{ color: theme.text }}>Visit #{visit.VISIT_ID}</p>
                                <p className="text-sm opacity-70 line-clamp-1" style={{ color: theme.textSecondary }}>
                                  <span className="font-semibold text-primary" style={{ color: theme.primary }}>Symptoms:</span> {visit.PATIENT_SYMPTOMS || "None recorded"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td style={tableCellStyle}>
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primary + '10' }}>
                                <Calendar size={16} style={{ color: theme.primary }} className="opacity-70" />
                              </div>
                              <span className="font-semibold text-base whitespace-nowrap" style={{ color: theme.text }}>
                                {visit.CREATED_AT ? new Date(visit.CREATED_AT).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : "Recently Added"}
                              </span>
                            </div>
                          </td>
                          <td style={{ ...tableCellStyle, textAlign: "right" }}>
                            <motion.button
                              whileHover={{ scale: 1.05, x: 5 }}
                              whileTap={{ scale: 0.95 }}
                              className="px-6 py-2.5 rounded-full text-sm font-bold border transition-all duration-200 flex items-center justify-center gap-2 float-right shadow-sm group-hover:shadow-md"
                              style={{
                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                                borderColor: theme.border,
                                color: theme.primary
                              }}
                            >
                              Details
                              <ChevronRight size={14} />
                            </motion.button>
                          </td>
                        </motion.tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="py-24 text-center">
                          <History className="mx-auto mb-4 opacity-20" size={64} style={{ color: theme.text }} />
                          <p className="text-xl font-bold opacity-30" style={{ color: theme.text }}>No visit records found for this case</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-4">
            <div className="sticky top-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-2xl" style={{ backgroundColor: theme.secondary + '40', color: "#ffffff" }}>
                  <ListRestart size={22} />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: "#ffffff" }}>Add Visit</h3>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="rounded-[2.5rem] p-6 shadow-2xl border transition-all duration-500 hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)]"
                style={{
                  backgroundColor: theme.card,
                  borderColor: theme.border,
                  backdropFilter: "blur(20px)"
                }}
              >
                <AddNewVisit
                  patientVisits={visits}
                  patientId={patientId}
                  caseId={caseId}
                  actionstatus="ADD_NEW_VISIT"
                />
              </motion.div>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-16 text-center text-xs opacity-30 font-medium tracking-widest uppercase" style={{ color: "#ffffff" }}>
          Amrutdhara Healthcare • Visit Timeline Interface
        </div>
      </div>
    </div>
  );
};

export default PatientVisits;

